import { useCallback, useEffect, useMemo, useState } from "react";
import { useLazySaveChatbotActivityDetailQuery } from "../features/Feature_GeneralAppElements/GeneralAppElements_Api";
import { E_ChannelOptions } from "../globalTypes/GlobalTypes";
import { useAppSelector } from "../redux-store/CustomReduxHooks";
import { serviceApp, serviceMob, serviceWeb } from "../utils/constants";
import {
  getLobShortFormToSearch,
  getMobileOperatingSystem,
  generateJWTChatBot,
  windowOpen,
} from "../utils/helper";
import useCmsPageVariables from "./useCmsPageVariables";
import { v4 as uuidV4 } from "uuid";
import { isEmpty } from "lodash";

const simplifyChatbotUrl = process.env.REACT_APP_SIMPLIFICHATBOT_URL;

const useSaveChatbotActivity = () => {
  const [finalUserName, setFinalUserName] = useState<string>("");
  const cmsData = useCmsPageVariables("chatBotIcon");
  const simplifyChatbotBaseUrl = useMemo(
    () => cmsData?.attributes?.url || simplifyChatbotUrl,
    [cmsData?.attributes?.url],
  );
  const { channel, userDetails } = useAppSelector((state) => state.CsAppSlice);
  const [saveChatBotActivity] = useLazySaveChatbotActivityDetailQuery();
  const customerAndLobDetails = useAppSelector(
    (state) => state.CsAppSlice.userDetails?.customerAndLobDetails,
  );
  const csAppSlice = useAppSelector((state) => state.CsAppSlice);

  const linkedLobs = useMemo(
    () => getLobShortFormToSearch(customerAndLobDetails || []),
    [customerAndLobDetails],
  );

  // Memoize platform detection outside of useCallback
  const platform = useMemo(() => {
    const osType = getMobileOperatingSystem();
    if (osType === "iOS" || osType === "Android") {
      return csAppSlice.NativeDeviceInfo?.deviceName
        ? osType === "iOS"
          ? "iosApp"
          : "androidApp"
        : osType === "iOS"
          ? "iOSbrowser"
          : "androidBrowser";
    }
    return "web";
  }, [csAppSlice.NativeDeviceInfo?.deviceName]);

  useEffect(() => {
    if (userDetails?.IS_NTB_USER && isEmpty(userDetails?.name)) {
      setFinalUserName("User");
    } else {
      const userName =
        customerAndLobDetails?.[0]?.NAME || userDetails?.name || "";
      setFinalUserName(userName);
    }
  }, [userDetails?.IS_NTB_USER, userDetails?.name, customerAndLobDetails]);

  const callSaveChatbotActivityAndRedirect = useCallback(async () => {
    const uuidValue = uuidV4();
    saveChatBotActivity({
      uuid: uuidValue,
      chatbotStartTime: new Date().toISOString(),
      chatbotEndTime: null,
      source: channel,
      errorCode: null,
      errorMessage: null,
    });

    let updatedChannel = "";
    if (channel === E_ChannelOptions.SUPER_APP) {
      updatedChannel = "ABCDService";
    } else {
      if (platform === "iosApp" || platform === "androidApp") {
        updatedChannel = serviceApp;
      } else if (platform === "iOSbrowser" || platform === "androidBrowser") {
        updatedChannel = serviceMob;
      } else {
        updatedChannel = serviceWeb;
      }
    }

    const body = {
      uuid:
        customerAndLobDetails?.[0]?.MOBILE || userDetails?.mobileNumber || "",
      channel: updatedChannel,
      iat: Math.floor(Date.now() / 1000),
      linkedLobs: linkedLobs,
      userName: finalUserName,
    };
    const jwt = await generateJWTChatBot(body);
    const url = `${simplifyChatbotBaseUrl}?param=${jwt}&source=service`;
    windowOpen(url, "_self", "noopener,noreferrer");
  }, [
    channel,
    platform,
    customerAndLobDetails,
    userDetails?.mobileNumber,
    linkedLobs,
    finalUserName,
    simplifyChatbotBaseUrl,
    saveChatBotActivity,
  ]);

  return { callSaveChatbotActivityAndRedirect };
};

export default useSaveChatbotActivity;
